'use client';
// Import client-side i18next instance
import '@/lib/i18n/client';
import { useTranslation } from 'react-i18next';
import styles from './faqs.module.css';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

// Italian Market FAQs Component
function ItalianMarketFaqs() {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const market = searchParams.get('market');

  return (
    <>
      <section className={styles.header}>
        <h1 className={styles.heading}>{t('travellers.faqs.italianMarket.heading')}</h1>
        <p className={styles.subheading}>{t('travellers.faqs.italianMarket.subheading')}</p>
      </section>

      <div className={styles.faqContainer}>
        {/* Eligibility FAQ */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.italianMarket.eligibility.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.italianMarket.eligibility.answer.intro')}</p>
            <ul className={styles.faqList}>
              <li>{t('travellers.faqs.italianMarket.eligibility.answer.criteria.item1')}</li>
              <li>{t('travellers.faqs.italianMarket.eligibility.answer.criteria.item2')}</li>
              <li>{t('travellers.faqs.italianMarket.eligibility.answer.criteria.item3')}</li>
              <li>{t('travellers.faqs.italianMarket.eligibility.answer.criteria.item4')}</li>
            </ul>
          </div>
        </div>

        {/* Difference between STAMP and others */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.italianMarket.stampDifference.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.italianMarket.stampDifference.answer.part1')}</p>
            <p>{t('travellers.faqs.italianMarket.stampDifference.answer.part2')}</p>
            <p>{t('travellers.faqs.italianMarket.stampDifference.answer.part3')}</p>
          </div>
        </div>

        {/* STAMP Fee */}
        <StampFeeSection market={market || 'it'} />

        {/* STAMP App */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.italianMarket.stampApp.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.italianMarket.stampApp.answer.part1')}</p>
            <h3>{t('travellers.faqs.italianMarket.stampApp.answer.benefits.heading')}</h3>
            <ul className={styles.faqList}>
              <li>{t('travellers.faqs.italianMarket.stampApp.answer.benefits.item1')}</li>
              <li>{t('travellers.faqs.italianMarket.stampApp.answer.benefits.item2')}</li>
              <li>{t('travellers.faqs.italianMarket.stampApp.answer.benefits.item3')}</li>
              <li>{t('travellers.faqs.italianMarket.stampApp.answer.benefits.item4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

// Spanish Market FAQs Component
function SpanishMarketFaqs() {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const market = searchParams.get('market');

  return (
    <>
      <section className={styles.header}>
        <h1 className={styles.heading}>{t('travellers.faqs.spanishMarket.heading')}</h1>
        <p className={styles.subheading}>{t('travellers.faqs.spanishMarket.subheading')}</p>
      </section>

      <div className={styles.faqContainer}>
        {/* Eligibility FAQ */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.spanishMarket.eligibility.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.spanishMarket.eligibility.answer.intro')}</p>
            <ul className={styles.faqList}>
              <li>{t('travellers.faqs.spanishMarket.eligibility.answer.criteria.item1')}</li>
              <li>{t('travellers.faqs.spanishMarket.eligibility.answer.criteria.item2')}</li>
              <li>{t('travellers.faqs.spanishMarket.eligibility.answer.criteria.item3')}</li>
              <li>{t('travellers.faqs.spanishMarket.eligibility.answer.criteria.item4')}</li>
            </ul>
          </div>
        </div>

        {/* Difference between STAMP and others */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.spanishMarket.stampDifference.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.spanishMarket.stampDifference.answer.part1')}</p>
            <p>{t('travellers.faqs.spanishMarket.stampDifference.answer.part2')}</p>
            <p>{t('travellers.faqs.spanishMarket.stampDifference.answer.part3')}</p>
          </div>
        </div>

        {/* Invoice Types */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.spanishMarket.invoiceTypes.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.intro')}</p>

            <div className={styles.highlight}>
              <h3>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.realTaxFree.heading')}</h3>
              <p>
                {t('travellers.faqs.spanishMarket.invoiceTypes.answer.realTaxFree.description')}
              </p>
              <ul className={styles.faqList}>
                <li>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.realTaxFree.item1')}</li>
                <li>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.realTaxFree.item2')}</li>
              </ul>
            </div>

            <div className={styles.highlight}>
              <h3>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.taxRefund.heading')}</h3>
              <p>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.taxRefund.description')}</p>
              <ul className={styles.faqList}>
                <li>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.taxRefund.item1')}</li>
                <li>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.taxRefund.item2')}</li>
              </ul>
            </div>

            <p>{t('travellers.faqs.spanishMarket.invoiceTypes.answer.note')}</p>
          </div>
        </div>

        {/* STAMP Fee */}
        <StampFeeSection market={market || 'es'} />

        {/* First Purchase - Using Italian market content as fallback */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.italianMarket.firstPurchase.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.italianMarket.firstPurchase.answer.intro')}</p>
            <ul className={styles.faqList}>
              <li>{t('travellers.faqs.italianMarket.firstPurchase.answer.item1')}</li>
              <li>{t('travellers.faqs.italianMarket.firstPurchase.answer.item2')}</li>
              <li>{t('travellers.faqs.italianMarket.firstPurchase.answer.item3')}</li>
            </ul>
            <p>{t('travellers.faqs.italianMarket.firstPurchase.answer.alternative')}</p>
          </div>
        </div>

        {/* STAMP App */}
        <div className={styles.faqItem}>
          <h2 className={styles.faqQuestion}>
            {t('travellers.faqs.spanishMarket.stampApp.question')}
          </h2>
          <div className={styles.faqAnswer}>
            <p>{t('travellers.faqs.spanishMarket.stampApp.answer.part1')}</p>
            <h3>{t('travellers.faqs.spanishMarket.stampApp.answer.benefits.heading')}</h3>
            <ul className={styles.faqList}>
              <li>{t('travellers.faqs.spanishMarket.stampApp.answer.benefits.item1')}</li>
              <li>{t('travellers.faqs.spanishMarket.stampApp.answer.benefits.item2')}</li>
              <li>{t('travellers.faqs.spanishMarket.stampApp.answer.benefits.item3')}</li>
              <li>{t('travellers.faqs.spanishMarket.stampApp.answer.benefits.item4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

// Component for STAMP Fee section that dynamically uses the correct translation keys based on market
function StampFeeSection({ market }: { market: string }) {
  const { t } = useTranslation();
  const marketKey = market === 'it' ? 'italianMarket' : 'spanishMarket';

  return (
    <div className={styles.faqItem}>
      <h2 className={styles.faqQuestion}>{t(`travellers.faqs.${marketKey}.stampFee.question`)}</h2>
      <div className={styles.faqAnswer}>
        <p>{t(`travellers.faqs.${marketKey}.stampFee.answer.intro`)}</p>

        <h3>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.heading`)}</h3>
        <table className={styles.feeTable}>
          <tbody>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee1`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee2`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee3`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee4`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee5`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee6`)}</td>
            </tr>
            <tr>
              <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee7`)}</td>
            </tr>
            {marketKey === 'spanishMarket' && (
              <tr>
                <td>{t(`travellers.faqs.${marketKey}.stampFee.answer.belowTwentyK.fee8`)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <h3>{t(`travellers.faqs.${marketKey}.stampFee.answer.aboveTwentyK.heading`)}</h3>
        <p>{t(`travellers.faqs.${marketKey}.stampFee.answer.aboveTwentyK.description`)}</p>
      </div>
    </div>
  );
}

// Client Component that uses useSearchParams
function FaqsContent() {
  const searchParams = useSearchParams();
  const market = searchParams.get('market');

  return (
    <main className={styles.container}>
      {market === 'it' ? <ItalianMarketFaqs /> : <SpanishMarketFaqs />}
    </main>
  );
}

// Main FAQs Component with Suspense boundary
export default function Faqs() {
  return (
    <Suspense fallback={<div className={styles.container}>Loading...</div>}>
      <FaqsContent />
    </Suspense>
  );
}
